// Open the WC mini-cart drawer after a successful Add-to-Cart on PDP.
//
// UX rationale
// ------------
// Without visual feedback, customers who hit "Add to cart" on the PDP
// only see the button label flip to "X in cart" — easy to miss on
// mobile. The nav cart icon (`.wc-block-mini-cart__button`) opens a
// slide-in drawer; mirroring that exact same drawer opening on a
// successful add gives an immediate, on-brand confirmation.
//
// Scope: PDP only (where the WC Add-to-cart button lives). Buy Now is
// excluded — it redirects to /checkout/ on success, and flashing the
// drawer open just before navigation away is visual noise.
//
// How it works
// ------------
// 1. On click of the WC Add-to-cart button on a PDP, mark a "pending
//    add" flag with a short TTL.
// 2. Wrap window.fetch. When a `/wc/store/v1/cart/add-item` or
//    `/wc/store/v1/batch` request finishes successfully, AND the flag
//    is set, click the nav `.wc-block-mini-cart__button`.
// 3. WC's Interactivity API (`data-wp-on--click` on that button) fires
//    its drawer-open action — the same UX as if the customer clicked
//    the cart icon themselves.
//
// Composition with other fetch wrappers
// -------------------------------------
//   - fbq-bridge.js installs an outer fetch wrap to fire AddToCart pixel
//   - buy-now.js installs a temporary fetch wrap during its click flow
//   This module installs another outer wrap. Each preserves the
//   previous reference, so the chain composes: every wrap sees every
//   add-item request and can act on it.

const ADD_API_PATTERN = /\/wc\/store\/v1\/(cart\/add-item|batch)/;
const PENDING_TTL_MS  = 6000; // generous — covers slow networks + WC retries

// Defensive guard — only ever wrap once.
const FLAG = '__sentimentsCartDrawerOnAdd';

export function initCartDrawerOnAdd() {
	if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
	if (window[FLAG]) return;

	// Only wire on PDP — bail otherwise so we don't carry a permanent
	// fetch wrap onto cards / checkout / shop / etc. where it isn't
	// needed.
	const wcAddBtn = document.querySelector(
		'.mbf-single-product button.single_add_to_cart_button, ' +
		'.mbf-single-product .wc-block-components-product-button__button'
	);
	if (!wcAddBtn) return;

	window[FLAG] = true;

	let pendingUntil = 0;

	// Track only intentional Add-to-cart clicks. Buy-now sets
	// `data-busy="true"` on its own button before it synthetically
	// clicks the WC add-to-cart button — we check for that to skip
	// the drawer (Buy-now redirects to /checkout/, drawer would
	// flash for 100ms then disappear under the navigation).
	wcAddBtn.addEventListener('click', () => {
		const buyNowBusy = document.querySelector(
			'.sentiments-pdp-buy-now[data-busy="true"]'
		);
		if (buyNowBusy) return;
		pendingUntil = Date.now() + PENDING_TTL_MS;
	});

	const originalFetch = window.fetch;
	window.fetch = async function (...args) {
		const res = await originalFetch.apply(this, args);
		try {
			if (Date.now() > pendingUntil) return res;
			const url = getUrl(args);
			if (!url || !ADD_API_PATTERN.test(url)) return res;
			if (!res.ok) return res;

			// Inspect the response — if it carries an error code (e.g. the
			// "invalid_quantity" we saw on stock-limited variations), don't
			// open the drawer; the WC button will surface the error itself.
			const json = await res.clone().json();
			if (hasError(json)) return res;

			// Clear flag so a separate non-Add-to-cart request later in the
			// session doesn't accidentally open the drawer again.
			pendingUntil = 0;

			openMiniCart();
		} catch {
			// Swallow — never break a real fetch because the drawer-open
			// helper had a bad day.
		}
		return res;
	};
}

// Pull a human-friendly error indicator out of the WC batch response.
// Mirrors the logic in src/motion/buy-now.js (extractWcError) so the
// drawer doesn't flash open on an add that actually failed at the
// stock-check / nonce level.
function hasError(json) {
	if (!json) return false;
	const list = Array.isArray(json.responses) ? json.responses : [json];
	return list.some((r) => (r?.status >= 400) || r?.body?.code || r?.code);
}

function getUrl(args) {
	const first = args[0];
	if (typeof first === 'string') return first;
	if (first && typeof first.url === 'string') return first.url; // Request object
	return '';
}

function openMiniCart() {
	// WC's mini-cart trigger lives in the header. Calling .click() on it
	// fires its `data-wp-on--click` directive (WP Interactivity API), which
	// opens the drawer with the SAME animation as a real user click — what
	// the brief asks for.
	const btn = document.querySelector(
		'.wc-block-mini-cart__button, header .wc-block-mini-cart button'
	);
	if (!btn) return;

	// Wait a frame so WC's own post-add UI updates (the badge counter, the
	// "added" state on the button) land before the drawer opens — keeps
	// the drawer's initial paint consistent with the just-completed add.
	requestAnimationFrame(() => {
		btn.click();
	});
}
